/**
 * GLSL shaders for the GPU 3D typhoon particle system.
 *
 * Realistic typhoon structure:
 *  - Eye: calm hollow center, particles sink gently inside it
 *  - Eyewall: strongest tangential wind + updraft ring around the eye
 *  - Spiral rain bands: tangential speed is modulated by a rotating
 *    angular pattern so particles naturally clump into spiral arms
 *  - Outflow cap: above ~60% height the radial wind reverses to a
 *    strong outward spread, forming the anvil/cap on top
 *
 * No GLSL comments inside shader strings (Cesium removeComments bug).
 */

/* ------------------------------------------------------------------ */
/* Compute pass                                                        */
/* ------------------------------------------------------------------ */
export const computeFragmentShader = `
precision highp float;

uniform sampler2D u_particleTexture;
uniform vec2  u_texSize;
uniform float u_time;
uniform float u_dt;
uniform float u_speedFactor;
uniform float u_lifeDecay;
uniform float u_verticalScale;
uniform float u_vortexRadius;
uniform float u_swirlStrength;
uniform float u_inflowStrength;
uniform float u_updraftStrength;
uniform float u_environmentStrength;
uniform float u_fieldType;
uniform float u_regionalBaseSpeed;
uniform float u_regionalMeander;
uniform float u_regionalUpdraft;
uniform float u_regionalTurbulence;

float hash(float n) {
  return fract(sin(n) * 43758.5453123);
}

float hash2(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash2(i);
  float b = hash2(i + vec2(1.0, 0.0));
  float c = hash2(i + vec2(0.0, 1.0));
  float d = hash2(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm2(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 3; i++) {
    v += amp * noise2(p);
    p = p * 2.0 + vec2(13.7, 9.3);
    amp *= 0.5;
  }
  return v;
}

vec3 vortexWind(vec3 p) {
  vec2  delta = p.xy - vec2(0.5);
  float dist  = length(delta);
  float pz    = clamp(p.z, 0.0, 1.0);
  float angle = atan(delta.y, delta.x);

  float q = dist / max(u_vortexRadius, 0.001);

  vec2 tanDir = (dist > 1e-5) ? vec2(-delta.y, delta.x) / dist : vec2(0.0);
  vec2 radDir = (dist > 1e-5) ? delta / dist : vec2(0.0);

  float vortexW = 1.0 - smoothstep(1.3, 2.6, q);

  float eyeMask = smoothstep(0.18, 0.35, q);

  float bandW = 1.0 - 0.65 * smoothstep(0.8, 2.5, q);
  float band = 1.0 + 0.9 * bandW * pow(0.5 + 0.5 * sin(3.0 * angle + 6.0 * q), 3.0);

  float core  = q * exp(1.0 - q * q * 0.8);
  float swirl = u_swirlStrength * core * band * (1.0 - 0.35 * pz) * vortexW * 0.9;

  float inflowLow  = (1.0 - pz) * (1.0 - pz);
  float outflowCap = smoothstep(0.55, 0.85, pz);
  float radial = (-u_inflowStrength * inflowLow * exp(-q * q * 0.12)
                  + u_inflowStrength * 3.5 * outflowCap * exp(-q * q * 0.08)) * vortexW;

  float eyewallUp = exp(-2.5 * (q - 1.0) * (q - 1.0));
  float vertProf  = 1.0 - 0.6 * pz;
  float eyeSink   = -0.5 * exp(-6.0 * q * q);
  float w = u_updraftStrength * (eyewallUp * vertProf + eyeSink) * vortexW;

  vec2 envBase = vec2(0.75, -0.28);
  float n1 = fbm2(delta * 6.0 + vec2(u_time * 0.04, u_time * 0.025));
  float n2 = fbm2(delta * 13.0 - vec2(u_time * 0.05, 0.0));
  float ang = (n1 - 0.5) * 1.1 + (n2 - 0.5) * 0.5;
  float cs = cos(ang);
  float sn = sin(ang);
  vec2 envDir = vec2(envBase.x * cs - envBase.y * sn, envBase.x * sn + envBase.y * cs);
  float envSpeed = 0.65 + 0.7 * n2;
  vec2 env = u_environmentStrength * envDir * envSpeed * (0.4 + 0.6 * pz) * (1.0 - vortexW);

  vec3 wind = vec3(
    (swirl * tanDir.x + radial * radDir.x) * eyeMask + env.x,
    (swirl * tanDir.y + radial * radDir.y) * eyeMask + env.y,
    w * u_verticalScale
  );

  return wind;
}

vec3 regionalWind(vec3 p) {
  vec2 xy = p.xy;
  float pz = clamp(p.z, 0.0, 1.0);

  vec2 q = xy * 2.2;

  float t = u_time * 0.06;

  float meander = fbm2(q * 1.4 + vec2(t, 0.0)) - 0.5;

  float streak = fbm2(q * 3.0 + vec2(-t * 0.7, t * 0.5));
  float streak2 = fbm2(q * 5.0 + vec2(t * 0.4, -t * 0.3));

  float shear = 0.35 + 0.65 * pz;

  float speedMod = 0.35 + 1.25 * (0.5 * streak + 0.5 * streak2);

  float speed = u_regionalBaseSpeed * shear * speedMod;

  float ang = meander * u_regionalMeander
            + (fbm2(q * 7.0 + vec2(0.0, t * 1.2)) - 0.5) * 0.7;
  float cs = cos(ang);
  float sn = sin(ang);
  vec2 flowDir = vec2(cs, sn);

  float eps = 0.05;
  vec2 qr = q * 4.0 + vec2(t * 0.9, -t * 0.6);
  float nx = fbm2(qr + vec2(eps, 0.0)) - fbm2(qr - vec2(eps, 0.0));
  float ny = fbm2(qr + vec2(0.0, eps)) - fbm2(qr - vec2(0.0, eps));
  vec2 curl = vec2(ny, -nx) * u_regionalTurbulence * speed * 1.2;

  vec2 horizontal = flowDir * speed + curl;

  float vn = fbm2(q * 1.8 + vec2(t * 1.1, -t * 0.8)) - 0.5;
  float vertProf = 0.35 + 0.65 * sin(pz * 3.14159);
  float vertical = u_regionalUpdraft * vn * vertProf * 8.0;

  return vec3(horizontal, vertical * u_verticalScale);
}

void main() {
  vec2 uv    = gl_FragCoord.xy / u_texSize;
  vec4 data  = texture2D(u_particleTexture, uv);
  vec3 pos   = data.rgb;
  float life = data.a;
  float idx  = uv.x * u_texSize.x + uv.y * u_texSize.x * u_texSize.y;
  bool oob = pos.x < 0.0 || pos.x > 1.0 ||
             pos.y < 0.0 || pos.y > 1.0 ||
             pos.z < 0.0 || pos.z > 1.0;
  if (life <= 0.0 || oob) {
    float seed  = idx * 1.6180339887 + floor(u_time * 57.0);
    float angle = hash(seed * 5.127) * 6.28318;
    float rRand = hash(seed * 6.381);
    // 台风：40% 落在涡旋盘内，60% 分布在周围环境风区域；
    // 区域：在整个模拟域内均匀播种（方形分布，覆盖四角），占满全高度柱。
    if (u_fieldType < 0.5) {
      float r;
      if (rRand < 0.4) {
        r = mix(0.05, 0.25, sqrt(hash(seed * 7.011)));
      } else {
        r = mix(0.25, 0.98, hash(seed * 7.931));
      }
      pos = vec3(0.5 + r * cos(angle), 0.5 + r * sin(angle),
                 hash(seed * 3.713) * 0.12);
    } else {
      pos = vec3(mix(0.02, 0.98, hash(seed * 7.011)),
                 mix(0.02, 0.98, hash(seed * 7.931)),
                 hash(seed * 3.713));
    }
    life = 0.3 + 0.7 * hash(seed * 4.951);
  } else {
    vec3 wind = mix(vortexWind(pos), regionalWind(pos), u_fieldType);
    pos  += wind * (u_speedFactor * u_dt);
    life -= u_lifeDecay * u_dt;
  }
  gl_FragColor = vec4(pos, life);
}
`;

/* ------------------------------------------------------------------ */
/* Render pass                                                         */
/* ------------------------------------------------------------------ */
export const renderVertexShader = `
attribute vec2 a_particleUV;

uniform sampler2D u_particleTexture;
uniform vec4  u_lonLatRange;
uniform vec2  u_heightRange;
uniform float u_pointSize;
uniform float u_vortexRadius;
uniform float u_swirlStrength;
uniform float u_inflowStrength;
uniform float u_updraftStrength;
uniform float u_environmentStrength;
uniform float u_fieldType;
uniform float u_regionalBaseSpeed;
uniform float u_regionalMeander;
uniform float u_regionalUpdraft;
uniform float u_regionalTurbulence;
uniform float u_verticalScale;
uniform float u_speedFactor;
uniform float u_trailLength;
uniform float u_time;
uniform float u_arcMode;
uniform float u_arcHeight;

varying float v_life;
varying float v_speed;
varying float v_angle;
varying float v_height;
varying float v_curvature;

float hash2(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash2(i);
  float b = hash2(i + vec2(1.0, 0.0));
  float c = hash2(i + vec2(0.0, 1.0));
  float d = hash2(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm2(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 3; i++) {
    v += amp * noise2(p);
    p = p * 2.0 + vec2(13.7, 9.3);
    amp *= 0.5;
  }
  return v;
}

vec3 geodeticToEcef(float lon, float lat, float h) {
  float a  = 6378137.0;
  float f  = 1.0 / 298.257223563;
  float e2 = f * (2.0 - f);
  float slat = sin(lat);
  float clat = cos(lat);
  float N  = a / sqrt(1.0 - e2 * slat * slat);
  return vec3(
    (N + h) * clat * cos(lon),
    (N + h) * clat * sin(lon),
    (N * (1.0 - e2) + h) * slat
  );
}

vec3 vortexWind(vec3 p) {
  vec2  delta = p.xy - vec2(0.5);
  float dist  = length(delta);
  float pz    = clamp(p.z, 0.0, 1.0);
  float angle = atan(delta.y, delta.x);

  float q = dist / max(u_vortexRadius, 0.001);

  vec2 tanDir = (dist > 1e-5) ? vec2(-delta.y, delta.x) / dist : vec2(0.0);
  vec2 radDir = (dist > 1e-5) ? delta / dist : vec2(0.0);

  float vortexW = 1.0 - smoothstep(1.3, 2.6, q);

  float eyeMask = smoothstep(0.18, 0.35, q);

  float bandW = 1.0 - 0.65 * smoothstep(0.8, 2.5, q);
  float band = 1.0 + 0.9 * bandW * pow(0.5 + 0.5 * sin(3.0 * angle + 6.0 * q), 3.0);

  float core  = q * exp(1.0 - q * q * 0.8);
  float swirl = u_swirlStrength * core * band * (1.0 - 0.35 * pz) * vortexW * 0.9;

  float inflowLow  = (1.0 - pz) * (1.0 - pz);
  float outflowCap = smoothstep(0.55, 0.85, pz);
  float radial = (-u_inflowStrength * inflowLow * exp(-q * q * 0.12)
                  + u_inflowStrength * 3.5 * outflowCap * exp(-q * q * 0.08)) * vortexW;

  float eyewallUp = exp(-2.5 * (q - 1.0) * (q - 1.0));
  float vertProf  = 1.0 - 0.6 * pz;
  float eyeSink   = -0.5 * exp(-6.0 * q * q);
  float w = u_updraftStrength * (eyewallUp * vertProf + eyeSink) * vortexW;

  vec2 envBase = vec2(0.75, -0.28);
  float n1 = fbm2(delta * 6.0 + vec2(u_time * 0.04, u_time * 0.025));
  float n2 = fbm2(delta * 13.0 - vec2(u_time * 0.05, 0.0));
  float ang = (n1 - 0.5) * 1.1 + (n2 - 0.5) * 0.5;
  float cs = cos(ang);
  float sn = sin(ang);
  vec2 envDir = vec2(envBase.x * cs - envBase.y * sn, envBase.x * sn + envBase.y * cs);
  float envSpeed = 0.65 + 0.7 * n2;
  vec2 env = u_environmentStrength * envDir * envSpeed * (0.4 + 0.6 * pz) * (1.0 - vortexW);

  vec3 wind = vec3(
    (swirl * tanDir.x + radial * radDir.x) * eyeMask + env.x,
    (swirl * tanDir.y + radial * radDir.y) * eyeMask + env.y,
    w * u_verticalScale
  );

  return wind;
}

vec3 regionalWind(vec3 p) {
  vec2 xy = p.xy;
  float pz = clamp(p.z, 0.0, 1.0);

  vec2 q = xy * 2.2;

  float t = u_time * 0.06;

  float meander = fbm2(q * 1.4 + vec2(t, 0.0)) - 0.5;

  float streak = fbm2(q * 3.0 + vec2(-t * 0.7, t * 0.5));
  float streak2 = fbm2(q * 5.0 + vec2(t * 0.4, -t * 0.3));

  float shear = 0.35 + 0.65 * pz;

  float speedMod = 0.35 + 1.25 * (0.5 * streak + 0.5 * streak2);

  float speed = u_regionalBaseSpeed * shear * speedMod;

  float ang = meander * u_regionalMeander
            + (fbm2(q * 7.0 + vec2(0.0, t * 1.2)) - 0.5) * 0.7;
  float cs = cos(ang);
  float sn = sin(ang);
  vec2 flowDir = vec2(cs, sn);

  float eps = 0.05;
  vec2 qr = q * 4.0 + vec2(t * 0.9, -t * 0.6);
  float nx = fbm2(qr + vec2(eps, 0.0)) - fbm2(qr - vec2(eps, 0.0));
  float ny = fbm2(qr + vec2(0.0, eps)) - fbm2(qr - vec2(0.0, eps));
  vec2 curl = vec2(ny, -nx) * u_regionalTurbulence * speed * 1.2;

  vec2 horizontal = flowDir * speed + curl;

  float vn = fbm2(q * 1.8 + vec2(t * 1.1, -t * 0.8)) - 0.5;
  float vertProf = 0.35 + 0.65 * sin(pz * 3.14159);
  float vertical = u_regionalUpdraft * vn * vertProf * 8.0;

  return vec3(horizontal, vertical * u_verticalScale);
}

void main() {
  vec4  data;
  vec3  norm;
  float life;

  if (u_arcMode > 0.5) {
    // Arc mode: sample wind field on a fixed UV grid, no advection.
    norm = vec3(a_particleUV, u_arcHeight);
    life = 1.0;
  } else {
    data = texture2D(u_particleTexture, a_particleUV);
    norm = clamp(data.rgb, 0.0, 1.0);
    life = data.a;
  }

  v_life   = clamp(life, 0.0, 1.0);
  v_height = norm.z;

  float lon = mix(u_lonLatRange.x, u_lonLatRange.z, norm.x);
  float lat = mix(u_lonLatRange.y, u_lonLatRange.w, norm.y);
  float h   = mix(u_heightRange.x, u_heightRange.y, norm.z);
  vec3 ecef = geodeticToEcef(lon, lat, h);

  vec3 wind = mix(vortexWind(norm), regionalWind(norm), u_fieldType);

  // Curvature: sample wind slightly ahead and compare angle.
  if (u_arcMode > 0.5) {
    vec3 aheadPos = clamp(norm + wind * 0.04, 0.0, 1.0);
    vec3 windAhead = mix(vortexWind(aheadPos), regionalWind(aheadPos), u_fieldType);
    float ang1 = atan(wind.y, wind.x);
    float ang2 = atan(windAhead.y, windAhead.x);
    float diff = atan(sin(ang2 - ang1), cos(ang2 - ang1));
    v_curvature = diff * 25.0;
  } else {
    v_curvature = 0.0;
  }

  float trailScale = (u_arcMode > 0.5) ? 0.02 : (u_speedFactor * u_trailLength);
  vec3 normTail = clamp(norm - wind * trailScale, 0.0, 1.0);
  float lon2 = mix(u_lonLatRange.x, u_lonLatRange.z, normTail.x);
  float lat2 = mix(u_lonLatRange.y, u_lonLatRange.w, normTail.y);
  float h2   = mix(u_heightRange.x, u_heightRange.y, normTail.z);
  vec3 ecef2 = geodeticToEcef(lon2, lat2, h2);

  vec4 view1 = czm_modelView * vec4(ecef, 1.0);
  vec4 view2 = czm_modelView * vec4(ecef2, 1.0);
  vec2 vdir  = view1.xy - view2.xy;
  v_angle = (length(vdir) > 1e-6) ? atan(vdir.y, vdir.x) : 0.0;

  vec2  delta = norm.xy - vec2(0.5);
  float q  = length(delta) / max(u_vortexRadius, 0.001);
  float vortexW = 1.0 - smoothstep(1.3, 2.6, q);
  float eyeMask = smoothstep(0.18, 0.35, q);
  float core  = q * exp(1.0 - q * q * 0.8);
  float vortexSpeed = clamp(
    u_swirlStrength * core * vortexW * eyeMask * 0.9 +
    u_environmentStrength * 0.8 * (1.0 - vortexW),
    0.0, 1.0
  );
  float regionalSpeed = clamp(length(wind.xy) * 0.7, 0.0, 1.0);
  v_speed = mix(vortexSpeed, regionalSpeed, u_fieldType);

  gl_Position  = czm_modelViewProjection * vec4(ecef, 1.0);
  gl_PointSize = max(u_pointSize * (0.5 + 0.5 * v_life), 2.0);
}
`;

export const renderFragmentShader = `
precision highp float;

uniform vec3  u_colorSlow;
uniform vec3  u_colorFast;
uniform float u_opacity;
uniform float u_arcMode;

varying float v_life;
varying float v_speed;
varying float v_angle;
varying float v_height;
varying float v_curvature;

void main() {
  vec2 coord = vec2(gl_PointCoord.x - 0.5, 0.5 - gl_PointCoord.y);

  float ca = cos(-v_angle);
  float sa = sin(-v_angle);
  vec2 rot = vec2(ca * coord.x - sa * coord.y, sa * coord.x + ca * coord.y);

  // Bend the streak into a parabolic arc using local wind curvature.
  float bend = v_curvature * rot.x * rot.x;
  vec2 bent = vec2(rot.x, rot.y + bend);

  // Arcs: longer and thinner than the flowing streak.
  float aspectX = u_arcMode > 0.5 ? 1.5 : 2.0;
  float aspectY = u_arcMode > 0.5 ? 10.0 : 6.0;
  vec2 sc = bent * vec2(aspectX, aspectY);
  float d = length(sc);
  if (d > 1.0) discard;

  float edge = 1.0 - d;
  edge = edge * edge;

  float ht = clamp(rot.x * 4.0 + 0.5, 0.0, 1.0);

  // 拉伸速度区间增强慢/快双色对比，并压低白色高光避免颜色被冲淡
  float spd = clamp(v_speed * 1.5 - 0.25, 0.0, 1.0);
  vec3 color = mix(u_colorSlow, u_colorFast, spd);
  color = mix(color, vec3(1.0), ht * 0.15 + spd * spd * 0.2);
  color = mix(color, vec3(0.9, 0.95, 1.0), v_height * 0.12);

  float lifeFade = clamp(v_life * 1.6, 0.0, 1.0);
  // Arcs: more uniform brightness along the length.
  float htWeight = u_arcMode > 0.5 ? 0.6 : (0.1 + 0.9 * ht);
  float alpha = edge * htWeight * lifeFade * u_opacity;

  gl_FragColor = vec4(color, alpha);
}
`;

/* ------------------------------------------------------------------ */
/* Heat volume pass (ray-marched 3D heatmap)                          */
/*                                                                    */
/* Renders the domain cube; the fragment shader marches a ray through */
/* the [0,1]^3 parameter space, analytically sampling the wind field  */
/* at each step and front-to-back compositing a turbo-colored density */
/* field. u_arcHeight controls how high the heat column rises.        */
/* ------------------------------------------------------------------ */
export const heatVertexShader = `
attribute vec3 a_localPos;

uniform vec4 u_lonLatRange;
uniform vec2 u_heightRange;

varying vec3 v_localPos;

vec3 geodeticToEcef(float lon, float lat, float h) {
  float a  = 6378137.0;
  float f  = 1.0 / 298.257223563;
  float e2 = f * (2.0 - f);
  float slat = sin(lat);
  float clat = cos(lat);
  float N  = a / sqrt(1.0 - e2 * slat * slat);
  return vec3(
    (N + h) * clat * cos(lon),
    (N + h) * clat * sin(lon),
    (N * (1.0 - e2) + h) * slat
  );
}

void main() {
  v_localPos = a_localPos;
  float lon = mix(u_lonLatRange.x, u_lonLatRange.z, a_localPos.x);
  float lat = mix(u_lonLatRange.y, u_lonLatRange.w, a_localPos.y);
  float h   = mix(u_heightRange.x, u_heightRange.y, a_localPos.z);
  vec3 ecef = geodeticToEcef(lon, lat, h);
  gl_Position = czm_modelViewProjection * vec4(ecef, 1.0);
}
`;

export const heatFragmentShader = `
precision highp float;

varying vec3 v_localPos;

uniform vec3  u_cameraLocal;
uniform float u_time;
uniform float u_fieldType;
uniform float u_vortexRadius;
uniform float u_swirlStrength;
uniform float u_inflowStrength;
uniform float u_updraftStrength;
uniform float u_environmentStrength;
uniform float u_regionalBaseSpeed;
uniform float u_regionalMeander;
uniform float u_regionalUpdraft;
uniform float u_regionalTurbulence;
uniform float u_verticalScale;
uniform float u_opacity;
uniform float u_arcHeight;

float hash2(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash2(i);
  float b = hash2(i + vec2(1.0, 0.0));
  float c = hash2(i + vec2(0.0, 1.0));
  float d = hash2(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// Heat shader uses 2 octaves (vs 3 in compute/render) since we only need magnitude.
float fbm2(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 2; i++) {
    v += amp * noise2(p);
    p = p * 2.0 + vec2(13.7, 9.3);
    amp *= 0.5;
  }
  return v;
}

vec3 vortexWind(vec3 p) {
  vec2  delta = p.xy - vec2(0.5);
  float dist  = length(delta);
  float pz    = clamp(p.z, 0.0, 1.0);
  float angle = atan(delta.y, delta.x);

  float q = dist / max(u_vortexRadius, 0.001);

  vec2 tanDir = (dist > 1e-5) ? vec2(-delta.y, delta.x) / dist : vec2(0.0);
  vec2 radDir = (dist > 1e-5) ? delta / dist : vec2(0.0);

  float vortexW = 1.0 - smoothstep(1.3, 2.6, q);
  float eyeMask = smoothstep(0.18, 0.35, q);
  float bandW = 1.0 - 0.65 * smoothstep(0.8, 2.5, q);
  float band = 1.0 + 0.9 * bandW * pow(0.5 + 0.5 * sin(3.0 * angle + 6.0 * q), 3.0);
  float core  = q * exp(1.0 - q * q * 0.8);
  float swirl = u_swirlStrength * core * band * (1.0 - 0.35 * pz) * vortexW * 0.9;

  float inflowLow  = (1.0 - pz) * (1.0 - pz);
  float outflowCap = smoothstep(0.55, 0.85, pz);
  float radial = (-u_inflowStrength * inflowLow * exp(-q * q * 0.12)
                  + u_inflowStrength * 3.5 * outflowCap * exp(-q * q * 0.08)) * vortexW;

  float eyewallUp = exp(-2.5 * (q - 1.0) * (q - 1.0));
  float vertProf  = 1.0 - 0.6 * pz;
  float eyeSink   = -0.5 * exp(-6.0 * q * q);
  float w = u_updraftStrength * (eyewallUp * vertProf + eyeSink) * vortexW;

  vec2 envBase = vec2(0.75, -0.28);
  float n1 = fbm2(delta * 6.0 + vec2(u_time * 0.2, u_time * 0.125));
  float n2 = fbm2(delta * 13.0 - vec2(u_time * 0.25, 0.0));
  float ang = (n1 - 0.5) * 1.1 + (n2 - 0.5) * 0.5;
  float cs = cos(ang);
  float sn = sin(ang);
  vec2 envDir = vec2(envBase.x * cs - envBase.y * sn, envBase.x * sn + envBase.y * cs);
  float envSpeed = 0.65 + 0.7 * n2;
  vec2 env = u_environmentStrength * envDir * envSpeed * (0.4 + 0.6 * pz) * (1.0 - vortexW);

  return vec3(
    (swirl * tanDir.x + radial * radDir.x) * eyeMask + env.x,
    (swirl * tanDir.y + radial * radDir.y) * eyeMask + env.y,
    w * u_verticalScale
  );
}

// Simplified regional wind for the heat shader: drops streak2, curl-noise
// and the vertical fbm2 used by the full field. Heat only needs |wind|
// approximately, so 2 fbm2 calls is enough for a believable heat cloud.
vec3 regionalWind(vec3 p) {
  vec2 xy = p.xy;
  float pz = clamp(p.z, 0.0, 1.0);
  vec2 q = xy * 2.2;
  float t = u_time * 0.3;

  float meander = fbm2(q * 1.4 + vec2(t, 0.0)) - 0.5;
  float streak  = fbm2(q * 3.0 + vec2(-t * 0.7, t * 0.5));
  float shear = 0.35 + 0.65 * pz;
  float speed = u_regionalBaseSpeed * shear * (0.35 + 1.25 * streak);

  float ang = meander * u_regionalMeander;
  vec2 horizontal = vec2(cos(ang), sin(ang)) * speed;

  float vertProf = 0.35 + 0.65 * sin(pz * 3.14159);
  float vertical = u_regionalUpdraft * (meander + 0.1) * vertProf * 8.0;

  return vec3(horizontal, vertical * u_verticalScale);
}

// Perceptual heatmap colormap: deep blue -> cyan -> green -> yellow -> red -> white.
vec3 turboColormap(float t) {
  t = clamp(t, 0.0, 1.0);
  vec3 c0 = vec3(0.05, 0.0, 0.40);
  vec3 c1 = vec3(0.0,  0.5, 1.0);
  vec3 c2 = vec3(0.0,  0.9, 0.4);
  vec3 c3 = vec3(1.0,  0.9, 0.0);
  vec3 c4 = vec3(1.0,  0.3, 0.0);
  vec3 c5 = vec3(1.0,  1.0, 1.0);
  vec3 col = mix(c0, c1, smoothstep(0.00, 0.20, t));
  col = mix(col, c2, smoothstep(0.20, 0.40, t));
  col = mix(col, c3, smoothstep(0.40, 0.60, t));
  col = mix(col, c4, smoothstep(0.60, 0.80, t));
  col = mix(col, c5, smoothstep(0.80, 1.00, t));
  return col;
}

void main() {
  vec3 entry = v_localPos;
  vec3 toFrag = v_localPos - u_cameraLocal;
  float toFragLen = length(toFrag);
  if (toFragLen < 1e-6) discard;
  vec3 dir = toFrag / toFragLen;

  // Ray-box intersection against the [0,1]^3 unit cube. Camera is assumed
  // to be outside the cube (BACK-face culling renders only the entry faces).
  vec3 tBot = (-entry) / dir;
  vec3 tTop = (1.0 - entry) / dir;
  vec3 tMax = max(tBot, tTop);
  float tExit = min(min(tMax.x, tMax.y), tMax.z);
  tExit = max(tExit, 0.0);
  if (tExit < 1e-4) discard;

  // Cheap pre-cull for vortex mode: only render fragments whose ray comes
  // close enough to the vortex center that heat is plausible. Use 4.0 * radius
  // so the env-wind heat (which extends beyond the vortex proper) is preserved.
  if (u_fieldType < 0.5) {
    vec2 delta = entry.xy - vec2(0.5);
    vec2 dirXY = dir.xy;
    float dirXYLen2 = dot(dirXY, dirXY);
    if (dirXYLen2 > 1e-6) {
      float tClosest = -dot(delta, dirXY) / dirXYLen2;
      tClosest = clamp(tClosest, 0.0, tExit);
      vec2 closestXY = entry.xy + dirXY * tClosest;
      if (length(closestXY - vec2(0.5)) > u_vortexRadius * 4.0) {
        discard;
      }
    }
  }

  // Constant 32 steps (was 64). Some drivers handle constant-bound loops
  // more reliably than adaptive breaks.
  const int STEPS = 32;
  float stepSize = tExit / float(STEPS);

  vec3 accumColor = vec3(0.0);
  float transmittance = 1.0;

  for (int i = 0; i < STEPS; i++) {
    float t = (float(i) + 0.5) * stepSize;
    vec3 p = entry + dir * t;

    // u_arcHeight controls the top of the heat column (fraction of full
    // height range). Soft fade so the column doesn't end with a hard line.
    float zMask = 1.0 - smoothstep(u_arcHeight * 0.85, u_arcHeight, p.z);
    if (zMask < 0.01) continue;

    vec3 wind = mix(vortexWind(p), regionalWind(p), u_fieldType);
    float speed = length(wind.xy);
    float wabs  = abs(wind.z);

    float intensity = clamp(speed * 0.9 + wabs * 0.4, 0.0, 1.0);
    if (intensity < 0.08) continue;

    // Density multiplier 0.20 (was 0.10) so heat accumulates to visible opacity.
    float density = smoothstep(0.08, 0.85, intensity) * 0.20 * zMask;
    vec3 heat = turboColormap(intensity);

    float a = density * transmittance;
    accumColor += heat * a;
    transmittance *= (1.0 - density);

    if (transmittance < 0.01) break;
  }

  float alpha = (1.0 - transmittance) * u_opacity;
  // Premultiplied alpha output (matches ONE / ONE_MINUS_SRC_ALPHA blending).
  // accumColor is already premultiplied (sum of heat*density*transmittance),
  // so scale both rgb and alpha by u_opacity uniformly.
  gl_FragColor = vec4(accumColor * u_opacity, alpha);
}
`;
